/*
eWorldES Proprietary Software
Copyright (C) 2025 eWorldES. All rights reserved.

This software is proprietary to eWorldES and is protected by copyright laws and international treaties.
You may not use, copy, modify, distribute, or disclose this software without explicit written permission from eWorldES.
Any unauthorized use or reproduction of this software is strictly prohibited and may result in legal action.

License Agreement:
By using this software, you agree to be bound by the terms of the eWorldES Proprietary Software License Agreement.
For a copy of the agreement, visit https://eworldes.com/ or contact eWorldES directly for more information.

This software is provided "AS IS" without warranty of any kind, express or implied, including but not limited to the warranties of
merchantability, fitness for a particular purpose, or non-infringement. In no event shall eWorldES be liable for any damages
arising out of the use of this software, even if eWorldES has been advised of the possibility of such damages.

For inquiries or requests regarding this software, please contact eWorldES at:
Email: info@eworldes.com
Website: https://eworldes.com/
*/
import { InputFormProps } from "./types";

import * as yup from "yup";

export const generateValidationSchema = (fields: InputFormProps[]) => {
  const validationSchema: any = {};

  fields.forEach((field) => {
    if (!field.dependentOn) {
      validationSchema[field.name] = fieldValidation(field);
    } else if (field.dependentOn && field.dependentOn.length > 0) {
      validationSchema[field.name] = yup.string().when(
        field.dependentOn.map((f) => `${f.field}`),
        {
          is: (...values: any) => {
            if (typeof field.dependentOn?.[0]?.value === "function") {
              return field.dependentOn?.[0]?.value(values);
            }
            if (Array.isArray(field.dependentOn?.[0]?.value)) {
              return field.dependentOn?.[0]?.value.some(values[0]);
            }
            return values[0] == field.dependentOn?.[0]?.value;
          },
          then: (schema: any) => {
            schema = fieldValidation(field);
            return schema;
          },
          otherwise: (schema) => schema,
        },
      );
    }
  });

  return yup.object(validationSchema);
};

const fieldValidation = (field: InputFormProps) => {
  let fieldValidation = yup.string();

  // Process default validations from the `validations` array
  if (field.validations) {
    field.validations.forEach((validation) => {
      switch (validation.type) {
        case "required":
          fieldValidation = fieldValidation.required(validation.message);
          break;
        case "isEmail":
          fieldValidation = fieldValidation.email(validation.message);
          break;
        case "minLength":
          fieldValidation = fieldValidation.min(
            validation.value as number,
            validation.message,
          );
          break;
        case "regex":
          fieldValidation = fieldValidation.matches(
            validation.regexp,
            validation.message,
          );
          break;
        default:
          break;
      }
    });
  }

  // Add custom validation if provided
  if (field.validator) {
    fieldValidation = fieldValidation.test(
      field.name,
      "Custom Validation Error",
      (value) => {
        const error = field.validator(value);
        if (error) {
          return false; // Validation failed
        }
        return true; // Validation passed
      },
    );
  }

  return fieldValidation;
};
